import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';

// The Leads overview. Search results link here as /leads?type=risk&key=R19 (search module); such a
// link goes straight to the record.
const SEARCH_KINDS: Readonly<Record<string, string>> = {
  risk: 'risks',
  acceptance: 'acceptances',
  ask: 'asks',
  decision: 'decisions',
  kpi: 'kpis',
  gate: 'gates',
  question: 'questions',
};
// The validated value is the table name, so it maps to itself.
const TYPES: Readonly<Record<string, string>> = {
  ...SEARCH_KINDS,
  ...Object.fromEntries(Object.values(SEARCH_KINDS).map((t) => [t, t])),
};

// Record key: matches "R19", "EXC-A1", "22"; rejects "../x", "R 1", "".
// https://regex101.com/?flavor=javascript&regex=%5E%5BA-Za-z0-9-%5D%7B1%2C32%7D%24&testString=R19%0AEXC-A1%0A..%2Fx
const KEY = /^[A-Za-z0-9-]{1,32}$/;

interface LeadsLink {
  readonly type?: string;
  readonly key?: string;
}

function validateLink(raw: Record<string, unknown>): LeadsLink {
  // Own keys only: "__proto__", "constructor" or "toString" must not resolve through the prototype.
  const type =
    typeof raw['type'] === 'string' && Object.hasOwn(TYPES, raw['type'])
      ? TYPES[raw['type']]
      : undefined;
  const key =
    typeof raw['key'] === 'string' || typeof raw['key'] === 'number' ? String(raw['key']) : '';
  return type !== undefined && KEY.test(key) ? { type, key } : {};
}

export const Route = createFileRoute('/_authed/leads/')({
  validateSearch: validateLink,
  // `search` here also carries the raw query from the root route, so it is checked again.
  beforeLoad: ({ search }) => {
    const link = validateLink(search as Record<string, unknown>);
    if (link.type === undefined || link.key === undefined) return;
    throw redirect({
      to: '/leads/$type/$key',
      params: { type: link.type, key: link.key },
      replace: true,
    });
  },
  component: lazyRouteComponent(() => import('../../../features/leads/screens.tsx'), 'LeadsIndex'),
});
